import { Component, Prop, Watch, Vue, Ref } from "vue-property-decorator";
import { mapState } from "vuex";
import InfiniteLoading from "vue-infinite-loading";
import RouteTranslations from "@/locales/routerTranslations";
import GenericCard from "@/components/stateless/GenericCard.vue";
import SearchFilters from "@/components/filters/SearchFilters.vue";
import {
  UtilitiesFunctions,
  UtilitiesRequests,
  ExperiencesRequests
} from "@/helpers";
import Experience from "@/models/experience";
import { Tag, CategoryTag } from "@/models/tag";
import Region from "@/models/region";
import SearchFiltersInfo from "@/components/filters/SearchFiltersInfo.vue";
import utilitiesFunctions from "@/helpers/utilitiesFunctions";
import { Route } from 'vue-router';
import ExperienceMap, { Bounds } from '@/components/experiences/experience-map/ExperienceMap';

@Component({
  components: {
    GenericCard,
    InfiniteLoading,
    SearchFilters,
    SearchFiltersInfo,
    ExperienceMap
  },
  computed: {
    ...mapState("app", ["tagsList"])
  }
})
export default class ExperiencesList extends Vue {
  showMap: boolean = false;
  zoomUnlocked: boolean = false;
  showFilterPanel: any[] = [];
  @Watch('showMap') showMapChanged() {
    if (!this.showMap) {
      this.zoomUnlocked = false;
      this.handleProcessFilters();
      return;
    }
    setTimeout(() => {
      this.adjustMapPosition();
      this.map.fit();
      setTimeout(() => this.zoomUnlocked = true, 500);
    }, 500);
  }

  tagsList!: CategoryTag[];
  loading: boolean = false;
  page: number = 1;
  translations: any = RouteTranslations;
  infiniteId = 0;
  moodId?: string;
  experiencesList: Array<any> = [];
  mapExperiences: Experience[] = [];
  tmpQueryString: string = "";
  experiencesTags: Tag[] = [];
  services: Tag[] = [];
  regions: Region[] = [];
  languages: Tag[] = [];
  moods: Tag[] = [];
  selectedMoodFromHome!: Tag;
  selectedExpFromHome: Tag[] = [];
  selectedRegionFromHome!: Region;
  filters: Object[] = [];
  filtersTimeout: any = null;
  filtersCallBack: Object[] = [
    {
      id: "refresh",
      text: "",
      icon: "refresh",
      callback: (): void => this.refreshFilters(),
      processFiltersCallback: (): void => this.handleProcessFilters()
    }
  ];
  filtersLoaded = false;
  total: number = 0;
  bounds!: Bounds;
  firstLoad: boolean = true;
  smAndDown: boolean = false;

  get listHidden() { return this.smAndDown && this.showMap }

  @Ref('experience-map') map!: ExperienceMap;

  @Watch('$route', { immediate: true, deep: true })
  onRouteChanged(route: Route) {
    if (route.name === 'experiences' && (route.query.term || (route.query.lat && route.query.lng))) this.processFilters();
  }

  handleProcessFilters(useDelay = true) {
    this.showFilterPanel = [];
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    if (useDelay) {
      clearTimeout(this.filtersTimeout);
      this.filtersTimeout = setTimeout(() => {
        this.processFilters();
      }, 1000);
    } else {
      this.processFilters();
    }
  }

  processFilters() {
    // console.warn("filter: ", val);
    // this.filtersUpdateList(val);
    const filters = [...this.filters];
    if (this.$route.query.term) {
      filters.push({
        id: filters.length,
        type: "input",
        label: '',
        queryParam: "term",
        inputType: "experiences",
        value: this.$route.query.term,
        charLimit: 2
      })
    }
    if (this.bounds && this.showMap) {
      filters.push(...Object.keys(this.bounds).map((k, i) => {
        return {
          id: filters.length + i,
          type: 'input',
          label: '',
          queryParam: k,
          inputType: 'experiences',
          value: this.bounds[k].toString() as any,
          charLimit: 1
        }
      }))
    }
    const query = this.$route.query, lat = query.lat, lng = query.lng;
    if (lat && lng) {
      filters.push({
        id: filters.length,
        type: "input",
        label: '',
        queryParam: "lat",
        inputType: "experiences",
        value: lat,
        charLimit: 2
      })
      filters.push({
        id: filters.length,
        type: "input",
        label: '',
        queryParam: "lng",
        inputType: "experiences",
        value: lng,
        charLimit: 2
      })
    }

    if (query.wineregion) {
      filters.push({
          id: filters.length,
          type: "selectArray",
          label: this.$t("searchFilters.regionLabel"),
          listType: "experiences",
          queryParam: "wineregion_ids",
          selectedVal: [{ id: query.wineregion }],
        })
    }

    if (query.product) {
      filters.push({
        id: filters.length,
        type: "selectArray",
        label: this.$t("searchFilters.regionLabel"),
        listType: "experiences",
        queryParam: "product_ids",
        selectedVal: [{ id: query.product }],
      })
    }


    if (query.city) {
      filters.push({
        id: filters.length,
        type: "input",
        label: '',
        queryParam: "city",
        inputType: "experiences",
        value: query.city,
        charLimit: 2
      })
    }
    const q = UtilitiesFunctions.buildFilteredRequest(filters);
    this.$store
        .dispatch("app/refreshSearchFilters", {
          queryString: q,
          filtersArray: JSON.parse(JSON.stringify(filters))
        })
        .then(() => {});
    if (this.tmpQueryString !== q) {
      this.page = 1;
      this.experiencesList = [];
      this.infiniteId += 1;
      // this.getExperiences();
      this.tmpQueryString = q;
    }
  }

  created() {
    console.log('created')
    // this.getFilters();
    if (this.$route.query.expType)
      this.$store.dispatch("app/refreshSearchFilters", {
        queryString: `expType=${this.$route.query.expType}`
      });
    if (this.$route.query.mood) {
      utilitiesFunctions.buildMoodTags().map((mood: any) => {
        if (mood.code === this.$route.query.mood) {
          this.moodId = mood.id;
          this.selectedMoodFromHome = mood;
        }
      });
    }
  }

  mounted() {
    window.addEventListener('resize', () => {
      this.adjustMapPosition()
    });
    this.getFilters().then(() => {
      this.handleProcessFilters(false);
    });
    this.adjustMapPosition();
  }

  adjustMapPosition() {
    this.smAndDown = document.body.clientWidth < 960;
    const mapEl = document.getElementsByClassName('experience-map').item(0) as HTMLElement;
    const listEl = document.getElementsByClassName('experience-list').item(0) as HTMLElement;
    if (!mapEl || !mapEl.style || !listEl || !listEl.style) {
      setTimeout(() => this.adjustMapPosition(), 100);
      return;
    }
    let offset = 10;
    if (this.smAndDown) offset = 0;
    mapEl.style.left = (listEl.offsetLeft + listEl.offsetWidth) + offset + 'px';
  }

  buildFilters() {
    this.filters = [
      {
        id: 7,
        type: "inputDate",
        label: 'Date',
        min: '2020-05-16',
        queryParam: ['date_start', 'date_end'],
        listType: "experiences",
        inputType: "experiences",
        selectedVal: [],
        range: true,
        charLimit: 1,
        cssClass: "xs12 shrink md2"
      },
      {
        id: 0,
        type: "selectArray",
        label: this.$t("searchFilters.regionLabel"),
        listType: "experiences",
        queryParam: "region_ids",
        itemList: this.regions,
        selectedVal: this.selectedRegionFromHome ? this.selectedRegionFromHome : [],
        filterParentId: null,
        cssClass: "xs12 shrink md2"
      },
      {
        id: 1,
        type: "selectArray",
        label: this.$t("searchFilters.experienceTypeLabel"),
        listType: "experiences",
        queryParam: "experience_type_ids",
        itemList: this.experiencesTags,
        selectedVal: this.selectedExpFromHome ? this.selectedExpFromHome : null,
        filterParentId: null,
        cssClass: "xs12 shrink md2"
      },
      {
        id: 2,
        type: "selectArray",
        label: this.$t("searchFilters.moodLabel"),
        listType: "experiences",
        queryParam: "mood_ids",
        itemList: this.moods,
        selectedVal: this.moodId ? [this.selectedMoodFromHome] : [],
        filterParentId: null,
        cssClass: "xs12 shrink md2"
      },
      {
        id: 3,
        type: "selectArray",
        label: this.$t("searchFilters.languagesLabel"),
        listType: "experiences",
        queryParam: "language_ids",
        itemList: this.languages,
        selectedVal: [],
        filterParentId: null,
        cssClass: "xs12 shrink md2"
      },
      {
        id: 4,
        type: "selectArray",
        label: this.$t("searchFilters.servicesLabel"),
        listType: "experiences",
        queryParam: "service_ids",
        itemList: this.services,
        selectedVal: [],
        filterParentId: null,
        cssClass: "xs12 shrink md2"
      },
      {
        id: 5,
        type: "input",
        label: '',
        queryParam: "price_min",
        inputType: "experiences",
        value: '',
        charLimit: 1
      },
      {
        id: 6,
        type: "input",
        label: '',
        queryParam: "price_max",
        inputType: "experiences",
        value: '',
        charLimit: 1
      }
    ];
    this.filtersLoaded = true;
  }

  getFilters() {
    return new Promise((resolve, reject) => {
      const allTags = this.tagsList;
      const regionsPromise = UtilitiesRequests.getRegionsV2();
      Promise.all([allTags, regionsPromise])
             .then(([tagsResponse, regionsResponse]) => {
               tagsResponse.forEach((category: any) => {
                 if (category.tags) {
                   switch (category.code) {
                     case "exptype":
                       this.experiencesTags.push(...category.tags);
                       this.selectedExpFromHome = this.experiencesTags.filter(
                         (experience: Tag) =>
                           this.$route.query.expType === experience.code
                       );
                       break;
                     case "services":
                       this.services.push(...category.tags);
                       break;
                     case "spklang":
                       this.languages.push(...category.tags);
                       break;
                     case "mood":
                       this.moods.push(...category.tags);
                       break;
                   }
                 }
               });
               this.regions = regionsResponse;

               /* checks to see if a region was selected in search */
               if (this.$route.query.region) {
                 this.selectedRegionFromHome = regionsResponse.filter((r: Region) => r.id === this.$route.query.region)
               }
               this.buildFilters();
               resolve();
             })
             .catch((error: any) => {
               this.buildFilters();
               this.filtersLoaded = true;
               this.$store.dispatch("app/setSnackbarMessage", {
                 message: error,
                 color: "red"
               });
               reject();
             });
    });
  }

  getExperiences($state?: any, filters?: any) {
    if (this.loading) return;
    this.loading = true;
    const experiences: any[] = this.page == 1 ? [] : this.experiencesList;
    ExperiencesRequests.fetchAll(filters)
                       .then((response: any) => {
                         if (response.data.length) {
                           this.page += 1;
                           experiences.push(...response.data);
                           this.experiencesList = experiences;
                           this.mapExperiences = this.experiencesList;
                           this.total = response.total;
                           $state && $state.loaded();
                         } else {
                           response.total > 0
                             ? (this.total = response.total)
                             : (this.total = 0);
                           $state && $state.complete();
                         }
                         $state && $state.loaded();
                         this.loading = false;
                         if (this.firstLoad) {
                           this.map.fit();
                           this.firstLoad = false;
                         }
                       })
                       .catch((error: Error) => {
                         this.loading = false;
                         this.$store.dispatch("app/setSnackbarMessage", {
                           message: error,
                           color: "red"
                         });
                       });
  }

  loadMoreExperiences($state?: any) {
    let filters = this.$store.state.app.searchFilters.queryString;
    filters = filters + "&page=" + this.page + "&per_page=48";
    this.getExperiences($state, filters);
  }

  mapBoundsChanged(bounds: Bounds) {
    this.bounds = bounds;
    this.processFilters();
  }

  openExperience(experience: Experience) {
    const toRoute = UtilitiesFunctions.handleRoute(experience.cardLink as string);
    const ref = this.$route.query.ref as string;
    const tap_test = this.$route.query.tap_test as string;
    let path = `${toRoute}/${experience.id}`;
    let routeData = this.$router.resolve({
      path,
      query: { ref, tap_test } }
    );
    window.open(routeData.href, "_blank");
  }

  beforeDestroy() {
    // if (
    //   this.$router.history.current.path !==
    //   '/amministrazione/elenco-iscrizioni' &&
    //   this.$router.history.current.name !== 'slug-slug1-id-edit'
    // ) {
    //   this.$store.dispatch('app/refreshPagination', null);
    //   this.$store.dispatch('app/refreshSearchFilters', {
    //     queryString: '',
    //     filtersArray: []
    //   });
    // }
  }

  // filtersUpdateList(val: any) {
  //   if (val[0] && val[0].selectedVal) {
  //     if (val[0].selectedVal.id !== val[1].filterParentId) {
  //       // const h = this.happenings.filter(
  //       //   (happening: Happening): boolean =>
  //       //     happening.eventId === val[0].selectedVal.id
  //       // );
  //       // val[1].filterParentId = val[0].selectedVal.id;
  //       // val[1].itemList = h;
  //     }
  //   }
  // }

  refreshFilters() {
    this.tmpQueryString = "";
    this.page = 1;
    this.$store
        .dispatch("app/refreshPagination", null)
        .then(() =>
          this.$store.dispatch("app/refreshSearchFilters", {
            queryString: "",
            filtersArray: []
          })
        )
        .then(() => this.getExperiences());
  }
}
